<script setup lang="ts">
import {useClipboard, useElementSize, useFullscreen, useWindowSize} from "@vueuse/core"
import Controls from "~/components/molecules/media-page/video-capture/Controls.vue"
import Dialog from "~/components/molecules/Dialog.vue"
import {useDirectoriesStore} from "~/stores/directories"
import QrcodeVue from "qrcode.vue"
import {type MediaVideoCaptureOptions, useMediaVideoCapture} from '~/composables/studio/useMediaVideoCapture'
import {useMediaPenTools} from "~/composables/studio/useMediaPenTools"
import {useMediaScreenshotTool} from "~/composables/studio/useMediaScreenshotTool"
import {useIfTeacher} from "~/composables/useIfTeacher"
import type {MediaPageData} from '~/types/media/MediaPageData'
import type {CapturePageData} from '~/types/media/CapturePageData'
import {
  CAPTURE_FLAG_DIALOG_DISPLAY_REQUESTED,
  TEACHER_ACCOUNT_REQUIRED_DISPLAY_REQUESTED
} from "~/types/bus-event-names"
import {useDirectoryStore} from "~/stores/directory";
import {useUserStore} from "~/stores/user";
import FlippedDirectoryDialogBody from "~/components/molecules/FlippedDirectoryDialogBody.vue";
import type {ShareData} from '~/types/media/ShareData'
import type {NavigationGuardNext, RouteLocationNormalized} from 'vue-router'
import {localeDirection} from '~/composables/useLocaleDirection'

const directoryAllowsCapture: boolean = useDirectoryStore().directoryAllowsCapture

const { initialize: initVideoCapture, isClosed, isCountingDown } = useMediaVideoCapture()
const { sidebar: sidebarSize } = useMediaCardSize()
const { downloadMediaScreenshot } = useMediaScreenshotTool()
const { setup: setupMediaPenTools, activePen, canUndo, undo: undoPenStroke, clear: clearPenStrokes } = useMediaPenTools()
const dataConverter = useDataConverter()
const { isSupported: clipboardIsSupported, copy, copied } = useClipboard()

const header = ref<HTMLDivElement|null>(null)
const studioToolbar = ref<HTMLDivElement|null>(null)
const titleBar = ref<HTMLDivElement|null>(null)
const toolbar = ref<HTMLDivElement|null>(null)
const favoriteDialog = ref<typeof Dialog|null>(null)
const shareDialog = ref<typeof Dialog|null>(null)
const captureControls = ref<typeof Controls|null>(null)
const flipDialog = ref<typeof Dialog|null>(null)
const flippedDirectoryDialogBody = ref<typeof FlippedDirectoryDialogBody|null>(null)
const fullscreenTarget = ref<HTMLDivElement|null>(null)

const frameWrapper = ref<HTMLDivElement|null>(null)
const mediaFrame = ref<HTMLIFrameElement|null>(null)

const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(fullscreenTarget)

const underMediaBar = computed(() => {
  return studioToolbar.value || titleBar.value
})

onMounted(() => {
  header.value = document.querySelector('.js-header')

  // Make sure to use the "hand" tool (no active pen)
  clearPenStrokes()

  // The frame is new of different from the previous one, we must set up the pen tools (canvas) again
  if (mediaFrame.value) {
    setupMediaPenTools(mediaFrame.value)
  }
})

const cookieWindowSize = useGlobalCookie('media-window-size', { default: () => {
    return { width: 800, height: 600}
  }
})

const { height: headerHeight } = useElementSize(header, {width: 0, height: 64})
const { height: underMediaBarHeight } = useElementSize(underMediaBar, {width: 0, height: 64})
const { width: windowWidth, height: windowHeight } = useWindowSize({includeScrollbar: false, listenOrientation: true, initialWidth: cookieWindowSize.value.width, initialHeight: cookieWindowSize.value.height})
const { width: toolbarWidth } = useElementSize(toolbar, {width: 64, height: 0})

watch([windowWidth, windowHeight], () => {
  cookieWindowSize.value = {
    width: windowWidth.value,
    height: windowHeight.value
  }
})

const maxFrameHeight = computed(() => {
  if (isFullscreen.value) {
    return windowHeight.value
  }

  return (windowHeight.value - headerHeight.value - underMediaBarHeight.value) - 155 // Arbitrary value to ensure we see a bit of what's under the media and its title
})

const maxFrameWidth = computed(() => {
  return windowWidth.value - toolbarWidth.value - 6
})

const mediaRatio = computed(() => {
  return props.media.width / props.media.height
})

const idealFrameSize = computed(() => {
  let w = maxFrameWidth.value
  let h = w / mediaRatio.value

  if (h > maxFrameHeight.value) {
    h = maxFrameHeight.value
    w = h * mediaRatio.value
  }

  return {
    width: Math.floor(w),
    height: Math.floor(h),
  }
})

const isSimulation = computed<boolean>(() => {
  return mediaIsSimulation(props.media)
})

const isCapture = computed<boolean>(() => {
  return mediaIsCapture(props.media)
})

const isVideo = computed<boolean>(() => {
  return mediaIsVideo(props.media)
})

function mediaIsCapture(media: MediaPageData|CapturePageData): media is CapturePageData {
  return media.mediaType === 'capture'
}

function mediaIsSimulation(media: MediaPageData|CapturePageData): media is MediaPageData {
  return media.mediaType === 'simulation'
}

function mediaIsVideo(media: MediaPageData|CapturePageData): media is MediaPageData {
  return media.mediaType === 'video'
}

const favoriteIcon = computed<string>(() => {
  return useDirectoriesStore().isMediaFavorite(dataConverter.pageToMediaCard(props.media)) ? 'star' : 'grade'
})

const props = defineProps<{
  media: MediaPageData|CapturePageData,
  context: 'media'|'capture'
}>()

const capture = computed<CapturePageData|null>(() => {
  return mediaIsCapture(props.media) ? props.media : null
})

const simulation = computed<MediaPageData|null>(() => {
  return mediaIsSimulation(props.media) ? props.media : null
})

const video = computed<MediaPageData|null>(() => {
  return mediaIsVideo(props.media) ? props.media : null
})

const withDescriptionMedia = computed<MediaPageData|null>(() => {
  return simulation.value ?
      simulation.value :
      (video.value && video.value.description) ?
          video.value :
          null
})

const shareData = computed<ShareData|null>(() => {
  if (simulation.value) {
    return simulation.value.share || null
  }

  if (video.value) {
    return video.value.share || null
  }

  if (capture.value) {
    return capture.value.share || null
  }

  return null
})

const shareEmbedHtml = computed(() => shareData.value ? `<iframe width=550 height=440 src="${shareData.value.frame}" frameborder=0></iframe>` : '')

const mediaTitle = computed(() => {
  return capture.value && capture.value.studentName ? `${capture.value.studentName} : ${capture.value.title}` : props.media.title
})

function penColorClass(c: string): string {
  return `relative after:content-[''] after:absolute ${c} after:top-[62.5%] after:left-[34%] after:w-[33%] after:h-[6.5%]`
}

const activePenStyle = "relative before:content-[''] before:absolute before:top-[10%] before:h-[80%] before:left-0 before:w-[8%] before:bg-domain before:rounded-r-bento"

function onStudioScreenCaptureClick() {
  if (!mediaFrame.value) {
    return
  }

  downloadMediaScreenshot(mediaFrame.value)
}

function onFavoriteClick() {
  useIfTeacher(() => {
    favoriteDialog.value?.show()
  })
}

function onShareClick() {
  if (!features.value.share.usable) {
    useNuxtApp().$mitt.emit(TEACHER_ACCOUNT_REQUIRED_DISPLAY_REQUESTED)
    return
  }
  shareDialog.value?.show()
}

function onFlipClick() {
  if (!features.value.flip.usable) {
    useNuxtApp().$mitt.emit(TEACHER_ACCOUNT_REQUIRED_DISPLAY_REQUESTED)
    return
  }
  flipDialog.value?.show()
}

function resetFlipDialog() {
  flippedDirectoryDialogBody.value?.reset()
}

function onCaptureToolsOpen() {
  if (!features.value.videoCapture.usable) {
    useNuxtApp().$mitt.emit(TEACHER_ACCOUNT_REQUIRED_DISPLAY_REQUESTED)
    return
  }

  if (frameWrapper.value && mediaFrame.value) {
    const options: MediaVideoCaptureOptions = {}
    if (simulation.value?.directoryOwnerStringRepresentation) {
      options.directoryOwnerStringRepresentation = simulation.value.directoryOwnerStringRepresentation
    }
    initVideoCapture(frameWrapper.value, mediaFrame.value, options).catch((e: Error) => {
      alert(`Error: ${e.message}`)
    })
  }
}

function onCaptureFlag() {
  useNuxtApp().$mitt.emit(CAPTURE_FLAG_DIALOG_DISPLAY_REQUESTED)
}

const { recordStep } = useMediaVideoCapture()

onBeforeRouteLeave((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  if (recordStep.value === 'recording') {
    alert(useNuxtApp().$i18n.t('capture.finish_before_leaving'))
    next(false)
    return
  }

  if (recordStep.value === 'idle' || recordStep.value === 'countdown') {
    recordStep.value = 'closed'
  }

  next()
})

const userIsAnonymousStudent = computed(() => {
  return useRoute().meta.userIsAnonymousStudent
})

const qrCodeOpen = ref(false)

watch(copied, (copied) => {
  if (copied) {
    showSuccessToast(useNuxtApp().$i18n.t('_global.copied'))
  }
})

type Action = {
  displayed: boolean
  usable: boolean
}
type ActionFeatures = {
  favorite: Action
  share: Action
  flip: Action
  videoCapture: Action
}
const features = computed<ActionFeatures>(() => {
  const userStore = useUserStore()
  const hideFavoriteTool = userStore.userData.features.hideFavoriteTool
  const hideShareTool = userStore.userData.features.hideShareTool
  const hideVideoCaptureTool = userStore.userData.features.hideVideoCaptureTool
  const hideFlipTool = userStore.userData.features.hideFlipTool

  return {
    favorite: {
      displayed: userStore.isAuthenticated && !hideFavoriteTool && !userStore.isLibrary,
      usable: true,
    },
    share: {
      displayed: userStore.isAuthenticated && !hideShareTool && !userStore.isLibrary,
      usable: Boolean(shareData.value),
    },
    flip: {
      displayed: isSimulation.value && userStore.isAuthenticated && !hideFlipTool && !userStore.isLibrary,
      usable: Boolean(userStore.isTeacher),
    },
    videoCapture: {
      displayed: isSimulation.value && (userStore.isAuthenticated || directoryAllowsCapture) && !hideVideoCaptureTool && !userStore.isLibrary,
      usable: Boolean(userStore.isTeacher) || directoryAllowsCapture,
    },
  }
})

const hasAnyAction = computed(() => {
  return Object.values(features.value).some((f) => f.displayed)
})

const mediaFrameSource = computed(() => {
  if (useRuntimeConfig().public.useLocalMediaProxy) {
    return `/api/media-proxy?media=${props.media.id}`
  }

  return props.media.frame
})
</script>

<template>
  <div class="flex flex-col gap-bento">
<!--    Media and tools-->
    <div class="flex gap-bento" ref="fullscreenTarget">
<!--      Media-->
      <div class="grow bg-domain-lighter rounded-bento overflow-hidden flex justify-center items-center z-above-page-fader" ref="frameWrapper">
        <div class="relative">
          <iframe :src="mediaFrameSource" frameborder="0" :width="idealFrameSize.width" :height="idealFrameSize.height" ref="mediaFrame"></iframe>
          <MoleculesMediaPageVideoCaptureCountdown v-if="isCountingDown"/>
        </div>
      </div>
<!--      Tools-->
      <div class="hidden md:flex flex-col gap-bento justify-center" ref="toolbar">
        <AtomsIconButton :icon="isFullscreen ? 'zoom_in_map' : 'zoom_out_map'" @click="toggleFullscreen"
                         v-tooltip.left="$t('media.tools.fullscreen')"/>
        <div v-if="isSimulation" class="flex flex-col bg-white rounded-bento z-above-page-fader">
          <AtomsIconButton :active="activePen === null" @click="activePen = null" icon="pan_tool_alt"
                           :active-style="activePenStyle"/>
          <AtomsIconButton :active="activePen === 'black'" @click="activePen = 'black'" icon="border_color"
                           :class="penColorClass('after:bg-black')" :active-style="activePenStyle"/>
          <AtomsIconButton :active="activePen === 'gold'" @click="activePen = 'gold'" icon="border_color"
                           :class="penColorClass('after:bg-gold')" :active-style="activePenStyle"/>
          <LayoutsSlideToggler :open="canUndo">
            <div class="flex flex-col">
              <AtomsIconButton icon="undo" :active-style="activePenStyle" @click="undoPenStroke"/>
              <AtomsIconButton icon="backspace" :active-style="activePenStyle" @click="clearPenStrokes"/>
            </div>
          </LayoutsSlideToggler>
        </div>
        <div v-if="isCapture" class="flex flex-col bg-white rounded-bento">
          <AtomsIconButton icon="flag" @click="onCaptureFlag" v-tooltip.left="$t('media.tools.flag')"/>
        </div>
        <div v-if="isSimulation" class="flex flex-col bg-white rounded-bento">
          <AtomsIconButton icon="photo_camera" @click="onStudioScreenCaptureClick"
                           v-tooltip.left="$t('media.tools.image_capture')"/>
        </div>
      </div>
    </div>
<!--    Studio tools-->
    <div ref="studioToolbar" v-if="hasAnyAction" class="flex gap-bento relative z-above-page-fader">
<!--      Allows centering-->
      <span class="hidden md:block mr-auto"></span>
      <AtomsButton v-if="features.favorite.displayed" class="grow md:grow-0" :no-label-on-small-screen="true"
                   hover-scheme="none" :before-icon="favoriteIcon" @click="onFavoriteClick"
                   :label="$t('media.tools.favorite')"/>
      <AtomsButton v-if="features.share.displayed" class="grow md:grow-0" :no-label-on-small-screen="true"
                   hover-scheme="none" before-icon="share" @click="onShareClick" :label="$t('_global.share')"/>
      <AtomsButton v-if="features.flip.displayed" class="grow md:grow-0" :no-label-on-small-screen="true"
                   :label="$t('flip.make')" hover-scheme="none" before-icon="graphic_eq" @click="onFlipClick"/>
      <AtomsButton v-if="features.videoCapture.displayed" class="hidden md:flex" :label="$t('capture.make')"
                   hover-scheme="none" before-icon="videocam" dark-color="domain" @click="onCaptureToolsOpen"
                   :class="{'opacity-50 pointer-events-none': !isClosed}"/>
      <!--      Allows centering and aligning to media tools column-->
      <span class="hidden md:block ml-auto min-w-button"></span>
      <MoleculesMediaPageVideoCaptureControls class="absolute w-full h-full" ref="captureControls" />
    </div>
<!--    Title bar-->
    <div ref="titleBar" class="flex gap-bento">
      <NuxtLink v-if="isCapture"
                :to="localePath({name: 'media-id-slug', params: {id: capture?.media?.id, slug: capture?.media?.slug}})">
        <img :src="media.thumbnailSizes.title" class="max-w-[64px] rounded-bento" alt="">
      </NuxtLink>
      <div v-else>
        <img :src="media.thumbnailSizes.title" class="max-w-[64px] rounded-bento" alt="">
      </div>
      <h1 class="grow bg-white rounded-bento padding-button px-[15px] py-[10px] text-button flex items-center font-bold">
        <span>{{ mediaTitle }}</span>
      </h1>
    </div>
<!--    Simulation content-->
    <div v-if="withDescriptionMedia" class="flex flex-col lg:flex-row gap-bento">
      <div class="flex flex-col grow gap-bento rounded-bento">
        <div class="p-6 bg-white rounded-bento grow">
          <div class="max-w-prose mx-auto" v-consent-module :dir="localeDirection">
            <AtomsMediaField :title="video ? $t('media.narration') : $t('media.summary')" :content="withDescriptionMedia.description"/>
            <AtomsMediaField :title="$t('media.goals')" :content="withDescriptionMedia.goals"/>
            <AtomsMediaField :title="$t('media.more')" :content="withDescriptionMedia.more"/>
            <AtomsMediaField :title="$t('media.scenario')" :content="withDescriptionMedia.scenario"/>
          </div>
        </div>
      </div>
      <div v-if="!userIsAnonymousStudent" class="lg:basis-[424px]">
        <div class="bg-white rounded-bento p-5 text-button mb-bento font-bold">{{ $t('media.further') }}</div>
        <LayoutsMediaPack>
          <AtomsMediaCard v-for="m in withDescriptionMedia.suggestions" :card-data="m" :class="sidebarSize" context="generic"/>
        </LayoutsMediaPack>
      </div>
    </div>
  </div>
  <MoleculesMediaPageVideoCaptureReviewDialog v-if="features.videoCapture.usable"/>
  <MoleculesDialog ref="favoriteDialog" v-if="features.favorite.usable">
    <MoleculesMediaCardDirectories class="min-w-[300px]" :media-data="dataConverter.pageToMediaCard(media)"
                                   context="dialog"/>
  </MoleculesDialog>
  <MoleculesDialog ref="shareDialog" v-if="features.share.displayed && features.share.usable">
    <div class="bg-white rounded-bento p-6" v-if="shareData">
      <AtomsHeadingsLarge class="mb-8">{{ $t('_global.share') }}</AtomsHeadingsLarge>
      <label class="block mb-5">
        <span class="flex text-button-small">
          <span class="font-bold mb-2 grow">{{ $t('media.authenticated_link') }}</span>
          <ClientOnly>
            <span v-if="clipboardIsSupported" class="text-domain cursor-pointer"
                  @click="copy(shareData.url)">{{ $t('_global.copy') }}</span>
          </ClientOnly>
        </span>
        <input class="block w-full max-w-lg border-2 border-black rounded-bento px-3 py-2" type="text"
               v-model="shareData.url" readonly>
      </label>
      <label class="block mb-5">
        <span class="flex text-button-small">
          <span class="font-bold mb-2 grow">{{ $t('media.embed') }}</span>
          <ClientOnly>
            <span v-if="clipboardIsSupported" class="text-domain cursor-pointer"
                  @click="copy(shareEmbedHtml)">{{ $t('_global.copy') }}</span>
          </ClientOnly>
        </span>
        <input class="block w-full max-w-lg border-2 border-black rounded-bento px-3 py-2" type="text"
               v-model="shareEmbedHtml" readonly>
      </label>
      <AtomsButton :label="$t('media.tools.qr_code')" @click="qrCodeOpen = !qrCodeOpen" before-icon="qr_code"
                   size="small"/>
      <LayoutsSlideToggler :open="qrCodeOpen">
        <qrcode-vue :value="shareData.url" :size="300" class="mt-5 max-w-full !h-auto"/>
      </LayoutsSlideToggler>
    </div>
  </MoleculesDialog>
  <MoleculesDialog ref="flipDialog" @close="resetFlipDialog" v-if="features.flip.usable">
    <MoleculesFlippedDirectoryDialogBody :media="media" ref="flippedDirectoryDialogBody"/>
  </MoleculesDialog>
  <MoleculesMediaPageVideoCaptureFlagDialog v-if="capture" :capture-code="capture.code"/>
</template>
